import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";

interface UseAsyncOptions {
  keepPreviousData?: boolean;
}

export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, dependencies: unknown[], options: UseAsyncOptions = {}) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const dataRef = useRef<T | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const keepPreviousData = options.keepPreviousData ?? false;

  const updateData = useCallback((value: SetStateAction<T | null>) => {
    setData((current) => {
      const next = typeof value === "function"
        ? (value as (previous: T | null) => T | null)(current)
        : value;
      dataRef.current = next;
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    const current = ++sequence.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const initialLoad = dataRef.current === null;
    if (initialLoad) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const result = await load(controller.signal);
      if (current === sequence.current) updateData(result);
    } catch (requestError) {
      if (current === sequence.current && !(requestError instanceof DOMException && requestError.name === "AbortError")) {
        setError(requestError instanceof Error ? requestError.message : "Something went wrong.");
      }
    } finally {
      if (current === sequence.current) {
        setLoading(false);
        setRefreshing(false);
      }
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [...dependencies, updateData]);

  useEffect(() => {
    if (!keepPreviousData) {
      dataRef.current = null;
      setData(null);
      setLoading(true);
      setRefreshing(false);
    }
    void refresh();
    return () => {
      sequence.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [refresh, keepPreviousData]);

  return { data, setData: updateData, loading, refreshing, error, refresh };
}
